import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import {
  FiSearch, FiUser, FiCheck, FiAlertTriangle, FiArrowLeft, FiX,
  FiShare2 as FiUpload, FiFileText, FiTrash2,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import '../../styles/Voice.css';
import '../../styles/QuoteProposal.css';
import { US_STATES, STATE_LABEL_BY_CODE, FEE_LIST, DriverRow } from './mvrData';
import { useToast } from '../../context/ToastContext';


const getToken     = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken')     || '';

interface Client { name: string; databaseId: string; eMail?: string; }

type Step   = 'client-select' | 'order-form';
type ApiType = 'ActivityHistory' | 'TransactionalMVR';

interface Props { onGoToHistory?: () => void; }

const threeYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 3);
  return d.toISOString().split('T')[0];
};

const MVRUpload: React.FC<Props> = ({ onGoToHistory }) => {
  const { showSuccess, showError } = useToast();
  const [step, setStep] = useState<Step>('client-select');

  /* Client search */
  const [clientQuery, setClientQuery]     = useState('');
  const [clientResults, setClientResults] = useState<Client[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  /* Driver list */
  const [driverMode, setDriverMode]               = useState<'existing' | 'new'>('existing');
  const [driverList, setDriverList]                 = useState<DriverRow[]>([]);
  const [driverListLoading, setDriverListLoading]   = useState(false);
  const [selectedDriver, setSelectedDriver]         = useState<DriverRow | null>(null);
  const [driverPage, setDriverPage]                 = useState(1);
  const [driverSortField, setDriverSortField]       = useState<keyof DriverRow | null>(null);
  const [driverSortDir, setDriverSortDir]           = useState<'asc' | 'desc'>('asc');
  const DRIVER_PAGE_SIZE = 10;

  /* Order form */
  const [apiType, setApiType]           = useState<ApiType>('ActivityHistory');
  const [licenseState, setLicenseState] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [firstName, setFirstName]       = useState('');
  const [middleName, setMiddleName]     = useState('');
  const [lastName, setLastName]         = useState('');
  const [birthDate, setBirthDate]       = useState('');
  const [address, setAddress]           = useState('');
  const [city, setCity]                 = useState('');
  const [formState, setFormState]       = useState('');
  const [zipcode, setZipcode]           = useState('');
  const [recordType, setRecordType]     = useState('');
  const [apiCost, setApiCost]           = useState(0);
  const [stateFee, setStateFee]         = useState(0);
  const [errors, setErrors]             = useState<Record<string, string>>({});
  const [locked, setLocked]             = useState(false);

  /* Confirm dialog */
  const [showConfirm, setShowConfirm]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [rateLimited, setRateLimited]   = useState(false);
  const [submitError, setSubmitError]   = useState('');

  /* Scan driving license */
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [scanFile, setScanFile]             = useState<File | null>(null);
  const [scanLoading, setScanLoading]       = useState(false);
  const [scanError, setScanError]           = useState('');
  const [isDragging, setIsDragging]         = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Success */
  const [successMessage, setSuccessMessage] = useState(false);

  /* ── Client search — debounced ── */
  useEffect(() => {
    if (!clientQuery.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      setClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI       || '')}/api/nowcert/customerlist`, {
        params: { searchText: clientQuery, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then(r => setClientResults(r.data || []))
        .catch(() => setClientResults([]))
        .finally(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [clientQuery]);

  const fetchDriverList = useCallback((insuredId: string) => {
    setDriverListLoading(true);
    setDriverList([]);
    setDriverPage(1);
    setDriverSortField(null);
    setDriverSortDir('asc');
    axios.get(`${(process.env.REACT_APP_NOWCERTSAPI       || '')}/api/nowcert/driverlist`, {
      params: { insuredId, ncToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => {
        const rows: DriverRow[] = Array.isArray(r.data)
          ? r.data.filter((d: DriverRow) => d.active === true || (d.active as unknown) === 'true')
          : [];
        setDriverList(rows);
      })
      .catch(() => setDriverList([]))
      .finally(() => setDriverListLoading(false));
  }, []);

  const selectClient = useCallback((client: Client) => {
    setSelectedClient(client);
    setClientQuery('');
    setClientResults([]);
    setDriverMode('existing');
    setSelectedDriver(null);
    fetchDriverList(client.databaseId);
  }, [fetchDriverList]);

  const onDriverSortClick = (field: keyof DriverRow) => {
    if (driverSortField === field) setDriverSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setDriverSortField(field); setDriverSortDir('asc'); }
  };

  const sortedDriverList = useMemo(() => {
    if (!driverSortField) return driverList;
    const field = driverSortField;
    const list = [...driverList];
    list.sort((a, b) => {
      const av = String(a[field] ?? '').toLowerCase();
      const bv = String(b[field] ?? '').toLowerCase();
      if (av < bv) return driverSortDir === 'asc' ? -1 : 1;
      if (av > bv) return driverSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [driverList, driverSortField, driverSortDir]);

  const driverTotalPages = Math.max(1, Math.ceil(sortedDriverList.length / DRIVER_PAGE_SIZE));
  const paginatedDrivers = sortedDriverList.slice((driverPage - 1) * DRIVER_PAGE_SIZE, driverPage * DRIVER_PAGE_SIZE);

  const onSelectDriverCheckbox = (d: DriverRow) => {
    setSelectedDriver(prev => (prev === d ? null : d));
  };

  const DriverSortHeader: React.FC<{ label: string; field: keyof DriverRow }> = ({ label, field }) => (
    <th
      className="qp-table__th"
      onClick={() => onDriverSortClick(field)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        {label}
        <span style={{ fontSize: 10, opacity: driverSortField === field ? 1 : 0.4 }}>
          {driverSortField === field ? (driverSortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );

  const applyDriverToForm = useCallback((driver: DriverRow | null) => {
    setFirstName(driver?.firstName || '');
    setMiddleName(driver?.middleName || '');
    setLastName(driver?.lastName || '');
    setLicenseNumber((driver?.licenseNumber || '').replace(/[\s-]/g, ''));
    setLicenseState(driver?.licenseState || '');
    setBirthDate(driver?.dateOfBirth ? driver.dateOfBirth.split('T')[0] : '');
    setAddress(driver?.addressLine1 || '');
    setCity(driver?.city || '');
    setFormState(driver?.state || '');
    setZipcode(driver?.zipCode || '');
  }, []);

  const onContinue = () => {
    if (driverMode === 'existing' && !selectedDriver) {
      showError('Please select a driver to continue.');
      return;
    }
    applyDriverToForm(driverMode === 'existing' ? selectedDriver : null);
    setErrors({});
    setStep('order-form');
  };

  const onBackToClient = () => {
    setStep('client-select');
    setSuccessMessage(false);
    setLocked(false);
  };

  /* ── Fee lookup for Transactional MVR ── */
  const feeOptionsForState = licenseState
    ? FEE_LIST.filter(f => f.state === STATE_LABEL_BY_CODE[licenseState])
    : [];

  const onRecordTypeChange = (value: string) => {
    setRecordType(value);
    const fee = feeOptionsForState.find(f => f.record_type === value);
    if (fee) {
      setApiCost(fee.cost + 3);
      setStateFee(fee.cost);
    }
  };

  /* ── Validation ── */
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = 'Please enter first name.';
    if (!lastName.trim())  errs.lastName  = 'Please enter last name.';
    if (!licenseState)     errs.licenseState = 'Please select license state.';
    if (!licenseNumber.trim()) errs.licenseNumber = 'Please enter license number.';
    if (apiType === 'TransactionalMVR' && !recordType) errs.recordType = 'Please select year.';
    if (!birthDate) {
      errs.birthDate = 'Please enter birth date.';
    } else {
      const years16Ago = new Date();
      years16Ago.setFullYear(years16Ago.getFullYear() - 16);
      const bd = new Date(birthDate);
      if (bd > new Date() || bd > years16Ago) {
        errs.birthDate = 'Birth date must be at least 16 years earlier than today.';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmitClick = () => {
    if (validate()) setShowConfirm(true);
  };

  const buildSelectionOptionJson = () => JSON.stringify({
    clientName: selectedClient?.name || '',
    selectedDriverDetails: driverMode === 'existing' ? selectedDriver : {},
    clientId: selectedClient?.databaseId || '',
  });

  const placeActivityHistoryOrder = useCallback(() => {
    setSubmitting(true);
    setSubmitError('');
    const data = {
      apiCost: 400,
      apiType: 'ActivityHistory',
      selectionOptionJson: buildSelectionOptionJson(),
      mvrRequest: {
        searchStartDate: threeYearsAgo(),
        licenseState,
        licenseNumber,
        birthDate,
        firstName,
        middleName,
        lastName,
        address: { street: address || '', city: city || '', state: formState || '', zipcode: (zipcode || '').replace('-', '') || '0' },
        billCode: 'Demo',
        billReference: 'Account',
      },
    };
    axios.post(`${(process.env.REACT_APP_DataFabric_Url   || '')}/api/mvr/placeorder`, data, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (res.data?.message == null) {
          setSuccessMessage(true);
          setLocked(true);
        } else {
          setSubmitError(res.data.message);
          showError(res.data.message);
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setSubmitting(false);
        if (err.response?.status === 429) {
          setRateLimited(true);
        } else {
          showError('Unknown error while placing the order.');
        }
      });
  }, [licenseState, licenseNumber, birthDate, firstName, middleName, lastName, address, city, formState, zipcode, selectedClient, selectedDriver, driverMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const placeTransactionalOrder = useCallback(() => {
    setSubmitting(true);
    setSubmitError('');
    const subType = recordType.split(' ')[0];
    const host = ['HI', 'MO', 'CA'].includes(licenseState) ? 'OVERNIGHT' : 'ONLINE';
    const data = {
      apiCost: Math.round(apiCost * 100),
      apiType: 'TransactionalMVR',
      selectionOptionJson: buildSelectionOptionJson(),
      transactionalMVRRequestModel: {
        searchStartDate: threeYearsAgo(),
        licenseState,
        licenseNumber,
        birthDate,
        firstName,
        middleName,
        lastName,
        address: { street: address || '', city: city || '', state: formState || '', zipcode: (zipcode || '').replace('-', '') || '0' },
        productId: 'DL',
        subType,
        host,
        purpose: 'EMPLOYMENT',
        billCode: 'Demo',
        billReference: 'Account',
      },
    };
    axios.post(`${(process.env.REACT_APP_DataFabric_Url   || '')}/api/mvr/mvrorder`, data, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (res.data?.message == null) {
          setSuccessMessage(true);
          setLocked(true);
        } else {
          setSubmitError(res.data.message);
          showError(res.data.message);
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setSubmitting(false);
        if (err.response?.status === 429) {
          setRateLimited(true);
        } else {
          showError('Unknown error while placing the MVR order.');
        }
      });
  }, [recordType, licenseState, licenseNumber, birthDate, firstName, middleName, lastName, address, city, formState, zipcode, apiCost, selectedClient, selectedDriver, driverMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirmSubmit = () => {
    setShowConfirm(false);
    if (apiType === 'ActivityHistory') placeActivityHistoryOrder();
    else placeTransactionalOrder();
  };

  /* ── Scan driving license (OCR) ── */
  const pickScanFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => /\.(pdf|jpe?g|png)$/i.test(f.name),
    );
    if (first) { setScanFile(first); setScanError(''); }
    else setScanError('Please select JPEG, PNG, JPG or PDF files only');
  }, []);

  const onScanUpload = useCallback(() => {
    if (!scanFile || !selectedClient) return;
    setScanLoading(true);
    setScanError('');
    const data = new FormData();
    data.append('drivingLicense', scanFile);
    data.append('selectedOption', JSON.stringify({
      useExistingInsured: false,
      insuredClientId: '',
      clientName: selectedClient.name,
    }));
    axios.post(`${(process.env.REACT_APP_PolicyAgent_Url   || '')}/scan_driving_license`, data, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        setScanLoading(false);
        if (res.data?.success) {
          const parsed = JSON.parse(res.data.drivingLicenseResponse);
          setFirstName(parsed.firstName || '');
          setLastName(parsed.lastName || '');
          setBirthDate(parsed.dateOfBirth ? parsed.dateOfBirth.split('T')[0] : '');
          setLicenseNumber((parsed.drivingLicenseNumber || '').replace(/[\s-]/g, ''));
          setAddress(parsed.addressLine1 || '');
          setCity(parsed.city || '');
          setFormState(parsed.state || '');
          setZipcode(parsed.zipCode || '');
          setLicenseState(parsed.state || '');
          setErrors({});
          setShowScanDialog(false);
          setScanFile(null);
          showSuccess('Driving license scanned successfully.');
        } else {
          showError(res.data?.errorMessage || 'Error while scanning the driving license.');
        }
      })
      .catch(() => {
        setScanLoading(false);
        showError('Error while creating the scan request.');
      });
  }, [scanFile, selectedClient]);

  const closeScanDialog = () => {
    setShowScanDialog(false);
    setScanFile(null);
    setScanError('');
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div className="ho-search" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>

        {/* ── STEP 1: Client + Driver Select ── */}
        {step === 'client-select' && (
          <div className="ho-card ho-card--form">
            <div className="ho-step-label"><span className="ho-step-num">1</span> Client</div>
            <div className="cs-client-search">
              <div className="cs-client-search__input-wrap">
                <FiSearch size={15} className="cs-client-search__icon" />
                <input
                  className="cs-client-search__input"
                  type="text"
                  placeholder="Search client name…"
                  value={clientQuery}
                  onChange={e => { setClientQuery(e.target.value); if (selectedClient) setSelectedClient(null); }}
                  autoFocus
                />
                {clientLoading && <span className="cs-client-search__spinner" />}
              </div>
              {clientResults.length > 0 && (
                <ul className="cs-client-dropdown">
                  {clientResults.map(c => (
                    <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => selectClient(c)}>
                      <span className="cs-client-dropdown__name">{c.name}</span>
                      {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {clientQuery.trim() && !clientLoading && clientResults.length === 0 && (
                <ul className="cs-client-dropdown">
                  <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>No clients found</li>
                </ul>
              )}
            </div>

            {selectedClient && (
              <>
                <div className="ho-step-divider" />
                <div className="ho-step-label"><span className="ho-step-num">2</span> Driver</div>
                <div className="ho-toggle-group">
                  <button
                    className={`ho-toggle${driverMode === 'existing' ? ' ho-toggle--active' : ''}`}
                    onClick={() => setDriverMode('existing')}
                  >
                    <FiSearch size={14} /> Existing Driver
                  </button>
                  <button
                    className={`ho-toggle${driverMode === 'new' ? ' ho-toggle--active' : ''}`}
                    onClick={() => { setDriverMode('new'); setSelectedDriver(null); }}
                  >
                    <FiUser size={14} /> New Driver
                  </button>
                </div>

                {driverMode === 'existing' && (
                  driverListLoading ? (
                    <div className="d-flex justify-content-center" style={{ padding: 20 }}><span className="cs-client-search__spinner" /></div>
                  ) : driverList.length === 0 ? (
                    <div className="qp-empty-state" style={{ padding: '24px 0' }}>
                      <FiUser size={24} />
                      <p>No active drivers found for this client.</p>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', margin: '2px 0 8px' }}>Existing Drivers List</p>
                      <div className="qp-table-wrap glass-surface">
                        <table className="qp-table">
                          <thead>
                            <tr>
                              <th className="qp-table__th" style={{ width: 60 }}>Select</th>
                              <DriverSortHeader label="First Name" field="firstName" />
                              <DriverSortHeader label="Middle Name" field="middleName" />
                              <DriverSortHeader label="Last Name" field="lastName" />
                              <DriverSortHeader label="License Number" field="licenseNumber" />
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedDrivers.map((d, i) => (
                              <tr
                                key={d.id || d.databaseId || i}
                                className="qp-table__row"
                                style={{ cursor: 'pointer' }}
                                onClick={() => onSelectDriverCheckbox(d)}
                              >
                                <td className="qp-table__td" onClick={e => e.stopPropagation()}>
                                  <input type="checkbox" checked={selectedDriver === d} onChange={() => onSelectDriverCheckbox(d)} />
                                </td>
                                <td className="qp-table__td">{d.firstName}</td>
                                <td className="qp-table__td">{d.middleName}</td>
                                <td className="qp-table__td">{d.lastName}</td>
                                <td className="qp-table__td">{d.licenseNumber}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {sortedDriverList.length > DRIVER_PAGE_SIZE && (
                        <div className="qp-pagination">
                          <span className="qp-pagination__info">
                            {(driverPage - 1) * DRIVER_PAGE_SIZE + 1}–{Math.min(driverPage * DRIVER_PAGE_SIZE, sortedDriverList.length)} of {sortedDriverList.length}
                          </span>
                          <div className="qp-pagination__controls">
                            <button className="qp-pagination__btn" disabled={driverPage === 1} onClick={() => setDriverPage(1)}>«</button>
                            <button className="qp-pagination__btn" disabled={driverPage === 1} onClick={() => setDriverPage(p => p - 1)}>‹</button>
                            <span className="qp-pagination__btn qp-pagination__btn--active">{driverPage}</span>
                            <button className="qp-pagination__btn" disabled={driverPage === driverTotalPages} onClick={() => setDriverPage(p => p + 1)}>›</button>
                            <button className="qp-pagination__btn" disabled={driverPage === driverTotalPages} onClick={() => setDriverPage(driverTotalPages)}>»</button>
                          </div>
                        </div>
                      )}
                    </>
                  )
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button className="cs-browse-btn" onClick={onContinue}>Continue →</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP 2: Order Form ── */}
        {step === 'order-form' && (
          <div className="ho-card ho-card--form">
            <div className="ho-result-topbar">
              <button className="ho-back-btn" onClick={onBackToClient} disabled={locked}>
                <FiArrowLeft size={14} /> Back
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Client: <strong style={{ color: 'var(--accent-purple)' }}>{selectedClient?.name}</strong>
              </span>
            </div>

            {successMessage ? (
              <div className="cs-success-banner-wrap">
                <div className="cs-success-banner">
                  <div className="cs-success-banner__icon">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="cs-success-banner__body">
                    <p className="cs-success-banner__title">Request submitted successfully!</p>
                    <p className="cs-success-banner__desc">
                      We are processing your data. Please check the{' '}
                      <button className="cs-success-link" onClick={onGoToHistory}>History</button>
                      {' '}tab to find out the current progress status.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="ho-toggle-group">
                  <button
                    className={`ho-toggle${apiType === 'ActivityHistory' ? ' ho-toggle--active' : ''}`}
                    onClick={() => setApiType('ActivityHistory')}
                    disabled={locked}
                  >
                    Activity History
                  </button>
                  <button
                    className={`ho-toggle${apiType === 'TransactionalMVR' ? ' ho-toggle--active' : ''}`}
                    onClick={() => setApiType('TransactionalMVR')}
                    disabled={locked}
                  >
                    Transactional MVR
                  </button>
                  {driverMode === 'new' && (
                    <button className="ho-link-btn" style={{ marginLeft: 'auto' }} onClick={() => setShowScanDialog(true)}>
                      <FiUpload size={13} /> Scan Driving License
                    </button>
                  )}
                </div>

                <div className="ho-form-grid">
                  <div className="ho-form-field">
                    <label className="ho-form-label">License State <span style={{ color: 'var(--accent-red, #ef4444)' }}>*</span></label>
                    <select
                      className={`ho-select${errors.licenseState ? ' ho-input--error' : ''}`}
                      value={licenseState}
                      onChange={e => { setLicenseState(e.target.value); setRecordType(''); setErrors(er => ({ ...er, licenseState: '' })); }}
                    >
                      <option value="">Select state</option>
                      {US_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    {errors.licenseState && <span className="ho-field-error">{errors.licenseState}</span>}
                  </div>

                  {apiType === 'ActivityHistory' ? (
                    <div className="ho-form-field">
                      <label className="ho-form-label">From Date</label>
                      <input className="ho-input" type="date" value={threeYearsAgo()} disabled />
                    </div>
                  ) : (
                    <div className="ho-form-field">
                      <label className="ho-form-label">Select Year <span style={{ color: 'var(--accent-red, #ef4444)' }}>*</span></label>
                      <select
                        className={`ho-select${errors.recordType ? ' ho-input--error' : ''}`}
                        value={recordType}
                        onChange={e => onRecordTypeChange(e.target.value)}
                      >
                        <option value="">Select year</option>
                        {feeOptionsForState.map(f => (
                          <option key={f.record_type} value={f.record_type}>{f.record_type}</option>
                        ))}
                      </select>
                      {errors.recordType && <span className="ho-field-error">{errors.recordType}</span>}
                    </div>
                  )}

                  <div className="ho-form-field">
                    <label className="ho-form-label">License Number <span style={{ color: 'var(--accent-red, #ef4444)' }}>*</span></label>
                    <input
                      className={`ho-input${errors.licenseNumber ? ' ho-input--error' : ''}`}
                      value={licenseNumber}
                      maxLength={20}
                      onChange={e => { setLicenseNumber(e.target.value); setErrors(er => ({ ...er, licenseNumber: '' })); }}
                      placeholder="Enter license number"
                    />
                    {errors.licenseNumber && <span className="ho-field-error">{errors.licenseNumber}</span>}
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">First Name <span style={{ color: 'var(--accent-red, #ef4444)' }}>*</span></label>
                    <input
                      className={`ho-input${errors.firstName ? ' ho-input--error' : ''}`}
                      value={firstName}
                      onChange={e => { setFirstName(e.target.value.replace(/[^a-zA-Z ]/g, '')); setErrors(er => ({ ...er, firstName: '' })); }}
                      placeholder="Enter first name"
                    />
                    {errors.firstName && <span className="ho-field-error">{errors.firstName}</span>}
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">Middle Name</label>
                    <input className="ho-input" value={middleName} onChange={e => setMiddleName(e.target.value.replace(/[^a-zA-Z ]/g, ''))} placeholder="Enter middle name" />
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">Last Name <span style={{ color: 'var(--accent-red, #ef4444)' }}>*</span></label>
                    <input
                      className={`ho-input${errors.lastName ? ' ho-input--error' : ''}`}
                      value={lastName}
                      onChange={e => { setLastName(e.target.value.replace(/[^a-zA-Z ]/g, '')); setErrors(er => ({ ...er, lastName: '' })); }}
                      placeholder="Enter last name"
                    />
                    {errors.lastName && <span className="ho-field-error">{errors.lastName}</span>}
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">Birth Date <span style={{ color: 'var(--accent-red, #ef4444)' }}>*</span></label>
                    <input
                      className={`ho-input${errors.birthDate ? ' ho-input--error' : ''}`}
                      type="date"
                      value={birthDate}
                      onChange={e => { setBirthDate(e.target.value); setErrors(er => ({ ...er, birthDate: '' })); }}
                    />
                    {errors.birthDate && <span className="ho-field-error">{errors.birthDate}</span>}
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">Address</label>
                    <input className="ho-input" value={address} onChange={e => setAddress(e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 100))} placeholder="Enter address" />
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">City</label>
                    <input className="ho-input" value={city} onChange={e => setCity(e.target.value.replace(/[^a-zA-Z ]/g, '').slice(0, 50))} placeholder="Enter city" />
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">State</label>
                    <select className="ho-select" value={formState} onChange={e => setFormState(e.target.value)}>
                      <option value="">Select state</option>
                      {US_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>

                  <div className="ho-form-field">
                    <label className="ho-form-label">Zip Code</label>
                    <input className="ho-input" value={zipcode} onChange={e => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 9))} placeholder="Enter zip code" />
                  </div>
                </div>

                {rateLimited && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
                    <FiAlertTriangle size={16} style={{ color: '#fbbf24', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Please wait 20 seconds before submitting another MVR request.</span>
                  </div>
                )}
                {submitError && <span style={{ fontSize: 12, color: 'var(--accent-red, #ef4444)' }}>{submitError}</span>}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <motion.button className="cs-btn-start" disabled={submitting} onClick={onSubmitClick} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    {submitting ? <span className="cs-upload-spinner" /> : 'Submit'}
                  </motion.button>
                </div>
              </>
            )}
          </div>
        )}
      </motion.div>

      {/* ── Confirmation modal ── */}
      {showConfirm && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(8, 6, 20, 0.92)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={() => setShowConfirm(false)}
        >
          <div className="ho-modal" style={{ width: 'min(420px, 90vw)' }} onClick={e => e.stopPropagation()}>
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <FiCheck size={16} className="ho-modal__icon" />
                <h3 className="ho-modal__title">Confirmation</h3>
              </div>
              <button className="ho-modal__close" onClick={() => setShowConfirm(false)}><FiX size={16} /></button>
            </div>
            <div className="ho-modal__body">
              <div style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 8, padding: 12, marginBottom: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
                Your account will be charged a total of{' '}
                <strong style={{ color: 'var(--text-primary)' }}>${apiType === 'ActivityHistory' ? '4' : apiCost.toFixed(2)}</strong>{' '}
                for this MVR record. Do you wish to proceed?
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Service Fee:</span>
                <span style={{ color: 'var(--text-primary)' }}>${apiType === 'ActivityHistory' ? '4.00' : '3.00'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: 'var(--text-secondary)' }}>State Fee:</span>
                <span style={{ color: 'var(--text-primary)' }}>${apiType === 'ActivityHistory' ? '0.00' : stateFee.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid var(--border-glass)', paddingTop: 10 }}>
                <span style={{ color: 'var(--text-primary)' }}>Total:</span>
                <span style={{ color: 'var(--text-primary)' }}>${apiType === 'ActivityHistory' ? '4.00' : apiCost.toFixed(2)}</span>
              </div>
            </div>
            <div className="ho-modal__footer" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConfirm(false)} style={{ padding: '8px 20px', fontSize: 13, fontWeight: 600, background: 'var(--surface-glass)', color: 'var(--text-secondary)', border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={onConfirmSubmit} className="cs-browse-btn">
                Ok
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Scan Driving License modal ── */}
      {showScanDialog && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(8, 6, 20, 0.92)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={closeScanDialog}
        >
          <div className="ho-modal" style={{ width: 'min(480px, 92vw)' }} onClick={e => e.stopPropagation()}>
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <FiUpload size={16} className="ho-modal__icon" />
                <h3 className="ho-modal__title">Upload a file</h3>
              </div>
              <button className="ho-modal__close" onClick={closeScanDialog}><FiX size={16} /></button>
            </div>
            <div className="ho-modal__body">
              <p style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', margin: '0 0 12px' }}>25 cent will be charged per usage</p>
              <div
                className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
                onDrop={e => { e.preventDefault(); setIsDragging(false); pickScanFile(e.dataTransfer.files); }}
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
              >
                {!scanFile ? (
                  <>
                    <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                    <p className="cs-dropzone__title">Drag & Drop your file here</p>
                    <p className="cs-dropzone__or">or</p>
                    <motion.button className="cs-browse-btn" onClick={() => fileInputRef.current?.click()} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                      Browse Files
                    </motion.button>
                    <p className="cs-dropzone__types">Allowed file types: PNG, JPG, JPEG</p>
                  </>
                ) : (
                  <div className="cs-file-card">
                    <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                    <div className="cs-file-card__info">
                      <span className="cs-file-card__name">{scanFile.name}</span>
                      <span className="cs-file-card__size">{(scanFile.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button className="cs-file-card__delete" onClick={() => setScanFile(null)} title="Remove file">
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files) pickScanFile(e.target.files); e.target.value = ''; }}
                />
              </div>
              {scanError && <p style={{ color: 'var(--accent-red, #ef4444)', fontSize: 12, marginTop: 10 }}>{scanError}</p>}
            </div>
            {scanFile && (
              <div className="ho-modal__footer" style={{ justifyContent: 'flex-end' }}>
                <motion.button className="cs-btn-start" disabled={scanLoading} onClick={onScanUpload} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  {scanLoading ? <span className="cs-upload-spinner" /> : 'Upload'}
                </motion.button>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default MVRUpload;

